import React from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { ApiPushPreviewAudience } from '../../apiService/Core/Apicall'
import PushPanel from './PushPanel'
import { InlineAlert } from './PushDisabledNotice'
import { fmtNum, getErrorMessage, useDebouncedValue } from './pushUtils'
import type { AudiencePayload, PreviewResult } from './types'

interface Props {
    audience: AudiencePayload
    empty: boolean
    /** Bump to force a fresh preview (e.g. after a 409). */
    refreshKey?: number
    onResult?: (result: PreviewResult | null) => void
}

const Stat = ({ label, value, strong }: { label: string; value: number; strong?: boolean }) => (
    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '10px', background: strong ? '#ff6a3d12' : '#1a1a1a', border: `1px solid ${strong ? '#ff6a3d40' : '#2a2a2a'}` }}>
        <Typography sx={{ fontSize: strong ? 24 : 20, fontWeight: 800, color: strong ? '#ff6a3d' : '#e0e0e0' }}>{fmtNum(value)}</Typography>
        <Typography sx={{ fontSize: 10, color: '#6a6a6a', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</Typography>
    </Box>
)

const AudiencePreview: React.FC<Props> = ({ audience, empty, refreshKey = 0, onResult }) => {
    const [result, setResult] = React.useState<PreviewResult | null>(null)
    const [error, setError] = React.useState<string | null>(null)
    const [loading, setLoading] = React.useState(false)
    const seq = React.useRef(0)
    const onResultRef = React.useRef(onResult)
    onResultRef.current = onResult

    const key = JSON.stringify(audience)
    const debouncedKey = useDebouncedValue(key, 600)

    React.useEffect(() => {
        if (empty) {
            seq.current += 1
            setResult(null)
            setError(null)
            setLoading(false)
            onResultRef.current?.(null)
            return
        }
        // the previous result no longer matches the selection while the debounce settles
        onResultRef.current?.(null)
        if (key !== debouncedKey) {
            seq.current += 1 // invalidate any in-flight response for the previous selection
            return
        }
        const mine = ++seq.current
        setError(null)
        setLoading(true)
        ApiPushPreviewAudience(JSON.parse(debouncedKey), { silent: true })
            .then((res) => {
                if (mine !== seq.current) return // stale response
                const r = (res?.data ?? null) as PreviewResult | null
                setResult(r)
                onResultRef.current?.(r)
            })
            .catch((e) => {
                if (mine !== seq.current) return
                setResult(null)
                setError(getErrorMessage(e))
                onResultRef.current?.(null)
            })
            .finally(() => {
                if (mine === seq.current) setLoading(false)
            })
    }, [key, debouncedKey, empty, refreshKey])

    React.useEffect(() => () => { seq.current += 1 }, [])

    const stale = key !== debouncedKey

    return (
        <PushPanel
            title="Audience preview"
            icon={<VisibilityOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}
            action={loading || stale ? <CircularProgress size={18} sx={{ color: '#ff6a3d' }} /> : null}
        >
            {empty ? (
                <Typography sx={{ fontSize: 13, color: '#6a6a6a' }}>Select at least one audience source to see a preview.</Typography>
            ) : error ? (
                <InlineAlert severity="error">{error}</InlineAlert>
            ) : !result ? (
                <Typography sx={{ fontSize: 13, color: '#6a6a6a' }}>Calculating...</Typography>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, opacity: stale ? 0.5 : 1 }}>
                    <Stat label="Total matched users" value={result.totalMatched} />
                    <Stat label="With eligible devices" value={result.withDevices} />
                    <Stat label="Without eligible devices" value={result.withoutDevices} />
                    <Stat label="Final sendable audience" value={result.sendable} strong />
                </Box>
            )}
            <Typography sx={{ fontSize: 11, color: '#5a5a5a', mt: 2 }}>
                The audience is frozen at the moment you confirm the send; later changes do not affect a running campaign.
            </Typography>
        </PushPanel>
    )
}

export default AudiencePreview
